import { createContext, PropsWithChildren, useEffect, useMemo } from "react";
import { useCookies } from "react-cookie";
import { IAuthenticatedUser, useAuthenticatedUser } from "../../hooks/useAuthenticatedUser";
import { IUserCompany, useCompanies, UserCompany } from "../../hooks/useCompanies";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { UserPermissions, UserRoles } from "../../utilities/userUtilities";
import { LoadingIndicator } from "../LoadingIndicator";

export interface IAppUser {
  permissions: UserPermissions;
  roles: UserRoles;
  activeCompany: IUserCompany;
}

export class AppUser implements IAppUser, IAuthenticatedUser {
  name = "";
  email = "";
  externalId = "";
  companyIds: number[] = [];
  claimsPermissions: Record<number, number[]> = {};
  permissions = new UserPermissions([]);
  roles = new UserRoles([]);
  activeCompany = UserCompany.EMPTY;

  private constructor(user: IAuthenticatedUser | null, activeCompany?: UserCompany | null) {
    if (user) {
      this.name = user.name;
      this.email = user.email;
      this.externalId = user.externalId;
      this.companyIds = user.companyIds;
      this.claimsPermissions = user.claimsPermissions;
      this.activeCompany = activeCompany ?? UserCompany.EMPTY;
      this.roles = new UserRoles(activeCompany?.role ? [activeCompany.role] : []);

      const permissions = user.claimsPermissions[this.activeCompany.id];
      this.permissions = new UserPermissions(permissions ?? []);
    }
  }

  static get EMPTY(): AppUser {
    return AppUser.create(null);
  }

  static create(user: IAuthenticatedUser | null, activeCompany?: UserCompany | null): AppUser {
    return new AppUser(user, activeCompany);
  }

  public isAdmin = (): boolean =>
    this.permissions.canAdminCompanies ||
    this.permissions.canAdminUsers ||
    this.permissions.canConfigureMargins ||
    this.permissions.canConfigureProducts;
}

interface IUserContext {
  user: AppUser;
  setCompany: (obj: any) => void;
}

export const UserContext = createContext<IUserContext>({} as IUserContext);

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const user = useAuthenticatedUser();
  const companies = useCompanies();

  // using localstorage so axios config has easy access to active company
  const [_context, setContext] = useLocalStorage("context", UserCompany.EMPTY);

  // using cookies so active company persists which switching between subdomains
  const [{ activeCompany }, setCookie] = useCookies(["activeCompany"]);

  useEffect(() => {
    if (activeCompany?.id) {
      setContext(activeCompany);
    }
  }, [activeCompany?.id]);

  // We are using a cookie here so this data is accessible across all subdomains
  // for whitelabeling. localstorage is strictly restricted by subdomain
  const setActiveCompany = (company: IUserCompany) => {
    const hostname = window.location.hostname;
    const rootDomain = parseRootDomain(hostname);

    setCookie("activeCompany", company, { domain: rootDomain, path: "/" });
  };

  const invalidCompany = activeCompany && !companies.some((c) => c.id === activeCompany.id);
  const noCompany = !activeCompany || activeCompany.id === 0;

  const emptyCompany = noCompany || invalidCompany;

  useEffect(() => {
    if (companies.length > 0 && emptyCompany) {
      companies.sort((a, b) => a.id - b.id);

      const defaultActiveCompany = UserCompany.clone(companies[0]);
      setActiveCompany(defaultActiveCompany);
    }
  }, [activeCompany, companies]);

  const companySubdomain = activeCompany?.subdomain;

  const isProd = !import.meta.env.VITE_TITLE;

  useEffect(() => {
    if (isProd && activeCompany) {
      ensureSubdomain(companySubdomain || "my");
    }
  }, [companySubdomain]);

  const userWithActiveCompany = useMemo(
    () => AppUser.create(user, activeCompany || null),
    [user, activeCompany?.id],
  );

  if (noCompany) {
    return <LoadingIndicator loading={true} />;
  }

  return (
    <UserContext.Provider value={{ user: userWithActiveCompany, setCompany: setActiveCompany }}>
      {children}
    </UserContext.Provider>
  );
};

function ensureSubdomain(correctSubdomain: string) {
  const { hostname, pathname, search } = window.location;

  const parts = hostname.split(".");
  const currentSubdomain = parts.length > 2 ? parts[0] : null;

  if (currentSubdomain !== correctSubdomain) {
    const domain = parts.slice(currentSubdomain ? 1 : 0).join(".");
    const newUrl = `https://${correctSubdomain}.${domain}${pathname}${search}`;

    window.location.href = newUrl;
  }
}

function parseRootDomain(hostname: string): string {
  const parts = hostname.split(".");

  // Determine the root domain (e.g., "example.com")
  // This works if the hostname has at least 2 parts.
  let rootDomain = hostname;
  if (parts.length >= 2) {
    rootDomain = parts.slice(-2).join(".");
  }

  return rootDomain;
}
